(ns practitest.frontend.components.overlay2
  (:require [reagent.core :as reagent]
            [reagent.dom]
            [goog.events :as gevents]
            ["react-dom" :as react-dom]
            ["@floating-ui/dom" :as fdom]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.common.utils :refer [register-components!]]))

(defn- unlisten! [holder]
  (when holder
    (when-let [id @holder]
      (utils/trace "removing listener" id)
      (gevents/unlistenByKey id))))

(defn- get-value [target]
  (cond
    (satisfies? IDeref target)
    (deref target)

    (ifn? target)
    (target)

    :else
    target))

(defn- create-portal [child container]
  (react-dom/createPortal child container))

;; Placements:
;type Placement =
;  | 'top'
;  | 'top-start'
;  | 'top-end'
;  | 'right'
;  | 'right-start'
;  | 'right-end'
;  | 'bottom'
;  | 'bottom-start'
;  | 'bottom-end'
;  | 'left'
;  | 'left-start'
;  | 'left-end';

;; Custom overlay component based on floating-ui positioning library
(defn ^{:component? true} floating-ui-overlay
  "Overlay implementation based on https://floating-ui.com/ library.

   Available properties:

   * `placement` - positioning of element, see https://floating-ui.com/docs/computePosition#placement
   * `show?` - boolean controlling visibility of dropdown body (always rendered, but hidden)
   * `on-request-close` - handler function to close dropdown (on e.g. outside click) - should switch `show?` property to false
   * `container` - atom with container ref to attach dropdown contents to (default is to render dropdown as child of target element)
   * `target` - atom with target element ref to compute positioning of dropdown against, optional, default is `children` parameter first DOM node
   * `offset` - offset of dropdown - see https://floating-ui.com/docs/offset
   * `shift?` - enable Shift middleware, see https://floating-ui.com/docs/shift
   * `flip` - enable Flip middleware, see https://floating-ui.com/docs/flip
   * `max-size?` - cap height to available space, pair with `.fui-max-size CSS`, see https://floating-ui.com/docs/size
   * `strategy` - positioning strategy - `\"absolute\"` | `\"fixed\"` see https://floating-ui.com/docs/computePosition#strategy
   * `target-width?` - match target element width
   * `trace?` - enable trace logging (only in dev builds)"
  [props
   children
   overlay-body]
  (let [local-target          (atom nil)
        wrapper-ref           (atom nil)
        listener-id           (atom nil)
        shadow-listener       (atom nil)
        last-props            (atom nil)
        component-holder      (atom nil)
        position              (atom {:x        0
                                     :y        0
                                     :placement nil
                                     :strategy (or (:strategy props)
                                                   "absolute")})

        get-target            (fn [props]
                                (if (contains? props :target)
                                  (let [target (get-value (:target props))]
                                    (if (nil? target)
                                      ;; TODO: some warning here ?
                                      ;; sometimes :ref handlers will be called with null value so you will need
                                      ;; to guard with (when node) clause to prevent false nil targets here
                                      @local-target
                                      target))
                                  @local-target))

        handle-reposition     (fn [{:keys [show?
                                           trace?
                                           placement
                                           offset
                                           shift?
                                           flip
                                           flip-padding
                                           max-size?
                                           on-placement-change]
                                    :as   props}]
                                (let [target (get-target props)]
                                  (utils/trace "reposition" show? target @wrapper-ref)

                                  (when (and target @wrapper-ref show?)
                                    (-> (fdom/computePosition target
                                                              @wrapper-ref
                                                              #js {:placement  placement
                                                                   :strategy   (or (:strategy props)
                                                                                   "absolute")
                                                                   :middleware #js [(when offset
                                                                                      (fdom/offset offset))
                                                                                    (when shift?
                                                                                      (fdom/shift))
                                                                                    (when flip
                                                                                      (if flip-padding
                                                                                        (fdom/flip #js {:padding flip-padding})
                                                                                        (fdom/flip)))
                                                                                    (when max-size?
                                                                                      (fdom/size
                                                                                        #js {:padding (or flip-padding 0)
                                                                                             :apply (fn [^js state]
                                                                                                      (set! (.. state -elements -floating -style -maxHeight)
                                                                                                            (str (js/Math.max 0 (.-availableHeight state)) "px")))}))]})
                                        (.then (fn [result]
                                                 (binding [utils/*enable-trace* trace?]
                                                   (utils/trace "compute-position" result)
                                                   ;; Actual placement - can change due to middleware work
                                                   (let [placement (.-placement result)
                                                         new {:x        (.-x result)
                                                              :y        (.-y result)
                                                              :placement placement
                                                              :strategy (.-strategy result)}]
                                                     (when (and (not= (:placement @position) placement)
                                                                on-placement-change)
                                                       (on-placement-change placement))

                                                     ;; We need to set those coordinates synchronously otherwise there
                                                     ;; would be annoying blinking on async re-renders
                                                     (js/Object.assign (.-style @wrapper-ref)
                                                                       #js {:left (str (.-x result) "px")
                                                                            :top  (str (.-y result) "px")})
                                                     (reset! position new)))))))))

        close-if-needed       (fn [type]
                                (let [props @last-props]
                                  ;; Only trigger if overlay is visible
                                  (when (:show? props)
                                    (utils/trace "requesting closing of overlay from" type)
                                    (when-let [handler (:on-request-close props)]
                                      (handler)))))

        handle-click-document (fn [e]
                                (binding [utils/*enable-trace* (:trace? @last-props)]
                                  (utils/trace "click-document" e)

                                  ;; In shadow DOM global document listener will be triggered for "click" events
                                  ;; but it's target would point to shadowRoot and not to actual element
                                  (let [target          (.-target e)
                                        shadow-root     (.-shadowRoot target)
                                        ;; Can be null for nested overlays
                                        position-target (get-target @last-props)]
                                    (if (nil? shadow-root)
                                      ;; Non-shadow DOM mode - check that click is outside overlay or trigger element
                                      (when (and @wrapper-ref
                                                 (not (.contains @wrapper-ref target))
                                                 position-target
                                                 (not (.contains position-target target)))
                                        (close-if-needed "global"))
                                      ;; shadow DOM mode - check if this particular shadow root contains
                                      ;; our trigger element and ignore that event (will be handled by separate function)
                                      (when (not (.contains shadow-root position-target))
                                        (close-if-needed "global"))))))

        handle-click-shadow   (fn [^js e]
                                (binding [utils/*enable-trace* (:trace? @last-props)]
                                  (utils/trace "click-shadow" e)

                                  (let [browser-event   (if (.-getBrowserEvent e)
                                                          (.getBrowserEvent e)
                                                          e)
                                        composed-path   (.composedPath browser-event)
                                        event-target    (when (pos? (.-length composed-path))
                                                          (aget composed-path 0))
                                        ;; Can be null for nested overlays
                                        position-target (get-target @last-props)]
                                    (utils/trace "event-target" event-target "position-target" position-target)
                                    (when (and @wrapper-ref
                                               event-target
                                               (not (.contains @wrapper-ref event-target))
                                               position-target
                                               (not (.contains position-target event-target)))
                                      (close-if-needed "shadow")))))

        handle-resize         (fn [e]
                                (when-let [this @component-holder]
                                  (let [props (reagent/props this)]
                                    (when (:show? props)
                                      (utils/trace "Repositioning visible overlay due to window resize")
                                      ;; Will trigger did-update, which in turn will call handle-reposition
                                      (reagent/force-update this)))))]
    (reagent/create-class
      {:component-did-mount    (fn [this]
                                 ;; Get children DOM node
                                 (reset! local-target (reagent.dom/dom-node this))
                                 (reset! component-holder this)

                                 (binding [utils/*enable-trace* (:trace? (reagent/props this))]
                                   (utils/trace "component-did-mount" @local-target @wrapper-ref)

                                   (let [props  (reagent/props this)
                                         target (get-target props)]

                                     ;; In case we got rendered shown initially
                                     (when (:show? props)
                                       (handle-reposition props)

                                       ;; Rerender to fix autofocus + target-width issue
                                       (when (:target-width? props)
                                         (reagent/force-update this)))

                                     (gevents/listen js/window "resize" handle-resize)
                                     ;; Install click listeners
                                     (reset! listener-id
                                             (gevents/listen js/document "click" handle-click-document))
                                     (when (dom/in-shadow-dom? target)
                                       ;; Install additional shadow DOM handler
                                       (reset! shadow-listener
                                               ;; We're using ".-host" here because sometimes shadowRoot is smaller
                                               ;; than actual host container and does not receive clicks properly
                                               ;; e.g. in header-bar-component in our case
                                               (gevents/listen (.-host (.getRootNode target))
                                                               "click" handle-click-shadow))))))

       :component-will-unmount (fn [this]
                                 (binding [utils/*enable-trace* (:trace? (reagent/props this))]
                                   (unlisten! listener-id)
                                   (unlisten! shadow-listener)
                                   (gevents/unlisten js/window "resize" handle-resize)))

       :component-did-update   (fn [this]
                                 (binding [utils/*enable-trace* (:trace? (reagent/props this))] 
                                   (when-not (and (instance? js/Node @local-target)
                                                  (.-isConnected @local-target))
                                     (reset! local-target (reagent.dom/dom-node this)))
                                   (utils/trace "did-update" @wrapper-ref)
                                   (handle-reposition (reagent/props this))))

       :reagent-render         (fn [{:keys [show?
                                            trace?
                                            container
                                            target-width?]
                                     :as   props}
                                    children
                                    overlay-body]

                                 (binding [utils/*enable-trace* trace?]
                                   (reset! last-props props)

                                   (utils/trace "render"
                                                (str @position)
                                                (str props))

                                   [:<>
                                    ;; Always render children, must be first element to prevent excessive DOM modifications
                                    children

                                    ;; TODO: animation
                                    (let [body [:div.dropdown-overlay
                                                {:ref   #(reset! wrapper-ref %)
                                                 :class (when (:max-size? props) "fui-max-size")
                                                 :style (merge
                                                          {
                                                           :position (:strategy @position)
                                                           :width    (let [target (get-target props)]
                                                                       (if (and target target-width?)
                                                                         ;; Same width as target element
                                                                         (.-offsetWidth target)
                                                                         "max-content"))
                                                           :left     (str (:x @position) "px")
                                                           :top      (str (:y @position) "px")
                                                           }

                                                          (when-not show?
                                                            {
                                                             ;; render hidden
                                                             :display "none"
                                                             ;:left    "-10000px"
                                                             ;:top     "-10000px"
                                                             }))}
                                                overlay-body]]

                                      (if container
                                        (let [actual-container (get-value container)]
                                          (utils/trace "container" container actual-container)
                                          (when actual-container
                                            ;; Render into portal if provided
                                            (create-portal
                                              (reagent/as-element body)
                                              actual-container)))
                                        body))]))})))

(register-components!)
